<script setup lang="ts">
import {onMounted, ref, watch} from 'vue'
import {collectionService, roleService} from '@/core/services'
import {useStore} from '@/core/store'
import {useRoute} from 'vue-router'
import Collection from '@/core/resources/Collection'
import {flashMessage} from '@/utils/FlashMessages'
import TiptapEditor from '@/components/TiptapEditor.vue'
import {popupUtil} from '@/utils/Popup'
import router from '@/router'
import {handleError} from '@/utils/ErrorHandler'
import TextInput from '@/components/input/TextInput.vue'
import Role from '@/core/resources/Role'
import description_info from '@/assets/files/collection_desc_info.html?raw'
import description_template from '@/assets/files/collection_desc_template.html?raw'
import {downloadFileNewTab} from "@/utils/fileUtil";

const collection = ref<Collection>(new Collection())
const showHTML = ref(true)
const file = ref<File | null>(null)

const store = useStore()
const route = useRoute()
const allRoles = ref<Role[]>([])

const compAny = ref(false)
const compMol = ref(false)
const compAtm = ref(false)

watch([compAny, compMol, compAtm], () => {
  collection.value.getLines = []

  if (compAny.value) {
    compAtm.value = compMol.value = false
    collection.value.getLines.push('any')
  }

  if (compMol.value) {
    compAny.value = false
    collection.value.getLines.push('molecular')
  }

  if (compAtm.value) {
    compAny.value = false
    collection.value.getLines.push('atomic')
  }
})

const id = ref(route.params.id)

onMounted(() => {
  roleService.getAll(store.getAccessToken()).then((roles) => {
    allRoles.value = roles.filter((r) => r.name !== 'Admin')
  })

  if (!id.value) return

  collectionService
      .get(id.value, store.getAccessToken())
      .then((c) => {
        collection.value = c
        if (collection.value.role === null) collection.value.role = ''

        const lines = collection.value.getLines
        if (lines.includes('any')) {
          compAny.value = true
        } else if (lines.includes('molecular') && lines.includes('atomic')) {
          compMol.value = true
          compAtm.value = true
        } else {
          if (lines.includes('molecular')) compMol.value = true
          if (lines.includes('atomic')) compAtm.value = true
        }
      })
      .catch(() => {
        router.push({name: 'collections'})
      })
})

const create = async () => {
  const confirmed = await popupUtil.confirm('Are you sure you want to create this collection?')

  if (!confirmed) return
  if (!file.value) {
    handleError(Error("README file is mandatory"))
    return
  }

  collectionService
      .create(collection.value, store.getAccessToken())
      .then((collection) => {
        collectionService.uploadReadmeFile(collection, file.value, store.getAccessToken())
      })
      .then(() => {
        flashMessage.success('Collection created successfully')
        router.push({name: 'collections'})
      })
      .catch(handleError)
}

const edit = async () => {
  const confirmed = await popupUtil.confirm('Are you sure you want to update this collection?')

  if (!confirmed) return

  collectionService
      .update(collection.value, store.getAccessToken())
      .then((collection) => {
        if (file.value) collectionService.uploadReadmeFile(collection, file.value, store.getAccessToken())
      })
      .then(() => {
        flashMessage.success('Collection updated successfully')
        router.push({name: 'collections'})
      })
      .catch(handleError)
}
const info = async () => {
  await popupUtil.info(description_info)
}

const loadHTMLTemplate = () => {
  collection.value.description = description_template
}

const getReadMe = () => {
  if (id.value) {
    collectionService
        .getReadmeFile(collection.value, store.getAccessToken())
        .then((result) => downloadFileNewTab(result, `README_${collection.value.name}.txt`))
        .catch(handleError)
  }
}
</script>

<template>
  <div class="container">
    <form novalidate>
      <h3 v-if="id">Edit collection</h3>
      <h3 v-else>Create collection</h3>

      <div class="form-group">
        <TextInput
            v-model="collection.name"
            :name="'name'"
            :id="'name'"
            :placeholder="'Name'"
            :type="'text'"
            :required="true"
            :disabled="!!id"/>

        <div id="description-title">
          <h4>Description</h4>
          <span class="material-symbols-outlined" @click="info">info</span>
        </div>

        <div id="html-buttons">
          <span><input type="checkbox" v-model="showHTML" id="show-html"/>Show HTML</span>
          <button type="button" v-if="showHTML" @click="loadHTMLTemplate">Load HTML Template</button>
        </div>

        <TiptapEditor v-model="collection.description" v-if="!showHTML"/>
        <textarea v-model="collection.description" v-else></textarea>

        <h4>Role</h4>
        <select v-model="collection.role" required id="role-select">
          <option value="" selected>No role</option>
          <option v-for="role in allRoles" :key="role.name" :value="role.name">{{ role.name }}</option>
        </select>

        <h4 :class="{clickable: id}" @click="getReadMe">READ_ME file</h4>
        <input type="file" accept=".txt" @change="file = ($event.target as HTMLInputElement).files![0]"
               :required="!id"/>

        <h4>Companion data</h4>
        <TextInput
            v-model="collection.companionModelAtmosphere"
            name="companionModelAtmosphere"
            id="companionModelAtmosphere"
            placeholder="Companion model atmosphere"
            type="text"/>

        <h4>Companion lines</h4>
        <div class="checkbox-group">
          <div>
            <input type="checkbox" v-model="compAny" id="companionAnyLines" :disabled="compMol || compAtm"/>
            <label for="companionAnyLines">Atomic & Molecular</label>
          </div>
          <div>
            <input type="checkbox" v-model="compAtm" id="companionAtomicLines" :disabled="compAny"/>
            <label for="companionAtomicLines">Atomic</label>
          </div>
          <div>
            <input type="checkbox" v-model="compMol" id="companionMolecularLines" :disabled="compAny"/>
            <label for="companionMolecularLines">Molecular</label>
          </div>
        </div>
      </div>

      <button v-if="id" type="button" class="submit-button" @click="edit">Update collection</button>
      <button v-else type="button" class="submit-button" @click="create">Create collection</button>
    </form>
  </div>
</template>

<style scoped lang="scss">
@import '@/assets/styles/input.scss';
@import '@/assets/styles/form.scss';

form {
  width: clamp(300px, 70%, 900px);
}

textarea {
  height: 236px;
  background-color: var(--theme-background);
  border: 1px solid var(--theme-border-color);
  border-radius: 8px;
  padding: 8px;
  font-size: 1rem;
  resize: none;
}

select {
  padding: 0.4rem;
  border: 1px solid var(--theme-border-color);
  border-radius: 0.2rem;
  width: fit-content;
  background-color: var(--theme-panel);

  transition: background-color 0.2s;

  &:disabled {
    background-color: var(--theme-panel-secondary);
    color: #666;
  }
}

#description-title {
  display: flex;
  align-items: center;
  gap: 5px;

  > span {
    cursor: pointer;
  }
}

#html-buttons {
  display: flex;
  gap: 5%;
}
</style>
